// src/services/bookingService.ts
import api from "../api/axiosClient";

const BASE = "/Booking";

// Normalize booking data to add default values for new fields
const normalizeBookingData = (booking: any) => {
  if (!booking) return booking;
  
  // Normalize booking items to include serviceCount and outsourcePersonIds
  // Backend Amount field already stores price per unit, so no division needed
  if (booking.bookingItems && Array.isArray(booking.bookingItems)) {
    booking.bookingItems = booking.bookingItems.map((item: any) => {
      const count = item.serviceCount ?? 1;
      const pricePerUnit = item.amount || 0; // Backend stores price per unit
      
      // Ensure outsourcePersonIds is always an array
      const outsourcePersonIds = item.outsourcePersonIds && Array.isArray(item.outsourcePersonIds) 
        ? item.outsourcePersonIds 
        : [];
      
      return {
        ...item,
        serviceCount: count,
        amount: pricePerUnit, // Already price per unit from backend
        outsourcePersonIds: outsourcePersonIds,
        // Ensure outsourcePersons is also an array
        outsourcePersons: item.outsourcePersons && Array.isArray(item.outsourcePersons)
          ? item.outsourcePersons
          : []
      };
    });
  }
  
  return booking;
};

// Remove new fields from payload if backend isn't ready (backward compatibility)
// Set this to true once backend database migration is complete
const BACKEND_SUPPORTS_SERVICE_COUNT = true;

const prepareBookingPayload = (data: any) => {
  if (!data) return data;
  
  // Handle both bookingItems and BookingItems (case-insensitive)
  const itemsKey = data.bookingItems ? 'bookingItems' : (data.BookingItems ? 'BookingItems' : null);
  
  // If backend doesn't support new fields yet, remove them
  if (!BACKEND_SUPPORTS_SERVICE_COUNT && itemsKey) {
    return {
      ...data,
      [itemsKey]: data[itemsKey].map((item: any) => {
        const { serviceCount, servicePrice, ...rest } = item;
        return rest;
      })
    };
  }
  
  // When backend supports ServiceCount, just pass through
  // Backend Amount field = price per unit, Backend will handle total calculation
  if (BACKEND_SUPPORTS_SERVICE_COUNT && itemsKey) {
    const processedItems = data[itemsKey].map((item: any) => {
      const count = item.serviceCount || 1;
      const pricePerUnit = item.amount || 0;
      
      // Ensure outsourcePersonIds is an array
      const outsourcePersonIds = item.outsourcePersonIds && Array.isArray(item.outsourcePersonIds)
        ? item.outsourcePersonIds
        : (item.OutsourcePersonIds && Array.isArray(item.OutsourcePersonIds)
          ? item.OutsourcePersonIds
          : []);
      
      return {
        ...item,
        amount: pricePerUnit, // Send price per unit (NOT multiplied)
        serviceCount: count,
        OutsourcePersonIds: outsourcePersonIds // Send array of IDs with uppercase for C# backend
      };
    });
    
    return {
      ...data,
      [itemsKey]: processedItems
    };
  }
  
  return data;
};

export const getArtistBookings = async () => {
  try {
    const res = await api.get(`${BASE}/artist`);
    const data = res.data;
    
    // Normalize array of bookings
    if (Array.isArray(data)) {
      return data.map(normalizeBookingData);
    }
    
    return data;
  } catch (error: any) {
    console.error('Error fetching artist bookings:', error);
    throw error;
  }
};

export const createBooking = async (data: any) => {
  const payload = prepareBookingPayload(data);
  const res = await api.post(`${BASE}/create`, payload);
  return res.data;
};

export const updateBooking = async (id: number, data: any) => {
  const payload = prepareBookingPayload(data);
  const res = await api.put(`${BASE}/update/${id}`, payload);
  return res.data;
};

export const saveBooking = async (data: any) => {
  const payload = prepareBookingPayload(data);
  // Unified wrapper: if id > 0, update; else create
  if (payload?.id && Number(payload.id) > 0) {
    const res = await api.put(`${BASE}/update/${payload.id}`, payload);
    return res.data;
  } else {
    const res = await api.post(`${BASE}/create`, payload);
    return res.data;
  }
};

export const getAllBookings = async () => {
  const res = await api.get(`${BASE}/all`);
  return res.data;
};

export const deleteBooking = async (id: number) => {
  const res = await api.delete(`${BASE}/${id}`);
  return res.data;
};

export const getBookingById = async (id: number) => {
  const res = await api.get(`${BASE}/${id}`);
  return normalizeBookingData(res.data);
};

export const getBookingReports = async (filters: any) => {
  try {
    const params = new URLSearchParams();
    if (filters.clientId) params.append('clientId', filters.clientId.toString());
    if (filters.year) params.append('year', filters.year.toString());
    if (filters.month) params.append('month', filters.month.toString());
    if (filters.serviceId) params.append('serviceId', filters.serviceId.toString());
    if (filters.outsourcePersonId) params.append('outsourcePersonId', filters.outsourcePersonId.toString());
    if (filters.status) params.append('status', filters.status);
    
    const url = `${BASE}/artist/reports${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await api.get(url);
    return res.data;
  } catch (error: any) {
    console.error('Error fetching booking reports:', error);
    throw error;
  }
};

export const getBookingReportFilters = async () => {
  try {
    const res = await api.get(`${BASE}/artist/reports/filters`);
    return res.data;
  } catch (error: any) {
    console.error('Error fetching report filters:', error);
    throw error;
  }
};
